import { useMemo, useState } from 'react';

import {
  useCampaigns,
  useFormResponses,
} from '../../hooks/useOutreach';
import type { FormResponse } from '../../types/outreach';
import { formatEt } from '../../utils/formatTimestamp';
import styles from '../pages/OutreachPage.module.css';
import { RefreshButton } from './RefreshButton';


type SortKey =
  | 'submitted_at' | 'recipient_name' | 'recipient_email' | 'company'
  | 'campaign_name' | 'attending';
type SortDir = 'asc' | 'desc';
type AttendingFilter = '' | 'yes' | 'no';

interface ColumnFilters {
  name: string;
  email: string;
  company: string;
  comments: string;
}

const EMPTY_FILTERS: ColumnFilters = {
  name: '', email: '', company: '', comments: '',
};


/**
 * Top-level Outreach > Responses tab. Cross-campaign listing of every
 * RSVP form submission (campaign_responses joined with recipient +
 * campaign info). Sort + per-column filter + campaign / attending
 * dropdowns mirror the recipients-table patterns elsewhere in the
 * outreach surface.
 *
 * Default ordering is server-side submitted_at desc; client-side
 * sort lets the user pivot by any column without a refetch.
 *
 * Distinct from FormsAdmin (which manages form *definitions*).
 */
export function ResponsesAdmin() {
  const campaignsQ = useCampaigns();
  // Server-side filters are the campaign + attending dropdowns. Free-
  // text column filters apply client-side over the returned page so
  // the user gets instant feedback while typing.
  const [campaignFilter, setCampaignFilter] = useState<number | ''>('');
  const [attendingFilter, setAttendingFilter] = useState<AttendingFilter>('');
  const responsesQ = useFormResponses({
    campaignId: campaignFilter === '' ? null : campaignFilter,
    attending: attendingFilter || null,
  });

  const [filters, setFilters] = useState<ColumnFilters>(EMPTY_FILTERS);
  const [sortKey, setSortKey] = useState<SortKey>('submitted_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const data = responsesQ.data;

  const filteredSorted = useMemo(() => {
    if (!data) return [];
    const norm = (s: string) => s.trim().toLowerCase();
    const matchText = (val: string | null, q: string) =>
      !q || (val || '').toLowerCase().includes(norm(q));
    const filtered = data.rows.filter((r) =>
      matchText(r.recipient_name, filters.name)
      && matchText(r.recipient_email, filters.email)
      && matchText(r.recipient_company, filters.company)
      && matchText(r.comments, filters.comments),
    );
    const dir = sortDir === 'asc' ? 1 : -1;
    const k = sortKey;
    const sortVal = (r: FormResponse): string | number => {
      switch (k) {
        case 'submitted_at':    return new Date(r.submitted_at).getTime();
        case 'recipient_name':  return (r.recipient_name || '').toLowerCase();
        case 'recipient_email': return r.recipient_email.toLowerCase();
        case 'company':         return (r.recipient_company || '').toLowerCase();
        case 'campaign_name':   return r.campaign_name.toLowerCase();
        case 'attending':       return r.attending || 'zzz';  // nulls last
      }
    };
    return [...filtered].sort((a, b) => {
      const va = sortVal(a);
      const vb = sortVal(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [data, filters, sortKey, sortDir]);

  function toggleSort(k: SortKey) {
    if (sortKey === k) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      // submitted_at defaults to desc (newest first); other columns
      // default to asc since alpha-up reads naturally.
      setSortDir(k === 'submitted_at' ? 'desc' : 'asc');
    }
  }

  // One chip column per session key present in the RESULT SET, in first-seen
  // order. Empty for every legacy row and every form without sessions, which
  // is what keeps the table shape unchanged where nothing uses sessions.
  const sessionKeys = useMemo(() => {
    const keys: string[] = [];
    for (const r of data?.rows || []) {
      for (const k of Object.keys(r.sessions || {})) {
        if (!keys.includes(k)) keys.push(k);
      }
    }
    return keys;
  }, [data]);

  const totalUnfiltered = data?.total ?? 0;
  const totalLoaded = data?.rows.length ?? 0;

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Form responses</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <RefreshButton
            onClick={() => responsesQ.refetch()}
            isFetching={responsesQ.isFetching}
            title="Re-fetch RSVP form submissions"
          />
        </div>
      </div>

      <div className={styles.helpText} style={{ marginBottom: 12 }}>
        Every RSVP form submission across all campaigns. Auto-refreshes
        every 30s while this tab is open. Click a column header to
        sort; per-column inputs filter client-side over the loaded page.
      </div>

      {/* Server-side filter chips: campaign + attending. Client-side
          column filters live in the table header below. */}
      <div
        style={{
          display: 'flex', gap: 12, flexWrap: 'wrap',
          alignItems: 'center', marginBottom: 12,
        }}
      >
        <label
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 13,
          }}
        >
          <span className={styles.label} style={{ marginBottom: 0 }}>
            Campaign:
          </span>
          <select
            className={styles.input}
            value={campaignFilter === '' ? '' : String(campaignFilter)}
            onChange={(e) =>
              setCampaignFilter(e.target.value === '' ? '' : Number(e.target.value))
            }
            style={{ fontSize: 12, padding: '3px 6px', minWidth: 200 }}
          >
            <option value="">All campaigns</option>
            {(campaignsQ.data || []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <div style={{ display: 'inline-flex', gap: 6 }}>
          {([
            { value: '', label: 'All' },
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ] as { value: AttendingFilter; label: string }[]).map((opt) => (
            <button
              key={opt.value || 'all'}
              type="button"
              className={`${styles.button} ${
                attendingFilter === opt.value ? styles.buttonPrimary : ''
              }`}
              onClick={() => setAttendingFilter(opt.value)}
              style={{ fontSize: 12, padding: '3px 10px' }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: '#666' }}>
          Showing {filteredSorted.length} of{' '}
          {totalLoaded < totalUnfiltered
            ? `${totalLoaded} loaded · ${totalUnfiltered} total`
            : totalUnfiltered}
        </span>
      </div>

      {responsesQ.isLoading ? (
        <div className={styles.empty}>Loading…</div>
      ) : responsesQ.isError ? (
        <div className={styles.empty} style={{ color: '#B04030' }}>
          Failed to load: {' '}
          {responsesQ.error instanceof Error
            ? responsesQ.error.message
            : 'unknown error'}
        </div>
      ) : !data || data.rows.length === 0 ? (
        <div className={styles.empty}>
          No form submissions yet.
          {totalUnfiltered === 0
            ? ' Submissions appear here as recipients RSVP via the email link or embed form.'
            : ' Try clearing filters.'}
        </div>
      ) : filteredSorted.length === 0 ? (
        <div className={styles.empty}>
          No submissions match the current filters.
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <SortHeader
                label="Submitted"
                k="submitted_at"
                {...{ sortKey, sortDir, toggleSort }}
              />
              <SortHeader
                label="Name"
                k="recipient_name"
                {...{ sortKey, sortDir, toggleSort }}
              />
              <SortHeader
                label="Email"
                k="recipient_email"
                {...{ sortKey, sortDir, toggleSort }}
              />
              <SortHeader
                label="Company"
                k="company"
                {...{ sortKey, sortDir, toggleSort }}
              />
              <SortHeader
                label="Attending"
                k="attending"
                {...{ sortKey, sortDir, toggleSort }}
              />
              {sessionKeys.map((k) => (
                <th key={k}>{k.replace(/_/g, ' ')}</th>
              ))}
              <th>Dietary</th>
              <th>Comments</th>
              <SortHeader
                label="Campaign"
                k="campaign_name"
                {...{ sortKey, sortDir, toggleSort }}
              />
            </tr>
            <tr>
              <th />
              <th>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="filter…"
                  value={filters.name}
                  onChange={(e) =>
                    setFilters({ ...filters, name: e.target.value })
                  }
                  style={{ width: '100%', fontSize: 11, padding: '3px 6px' }}
                />
              </th>
              <th>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="filter…"
                  value={filters.email}
                  onChange={(e) =>
                    setFilters({ ...filters, email: e.target.value })
                  }
                  style={{ width: '100%', fontSize: 11, padding: '3px 6px' }}
                />
              </th>
              <th>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="filter…"
                  value={filters.company}
                  onChange={(e) =>
                    setFilters({ ...filters, company: e.target.value })
                  }
                  style={{ width: '100%', fontSize: 11, padding: '3px 6px' }}
                />
              </th>
              <th />
              {sessionKeys.map((k) => (
                <th key={k} />
              ))}
              <th />
              <th>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="filter…"
                  value={filters.comments}
                  onChange={(e) =>
                    setFilters({ ...filters, comments: e.target.value })
                  }
                  style={{ width: '100%', fontSize: 11, padding: '3px 6px' }}
                />
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filteredSorted.map((r) => (
              <ResponseRow key={r.id} r={r} sessionKeys={sessionKeys} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


function SortHeader({
  label, k, sortKey, sortDir, toggleSort,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  toggleSort: (k: SortKey) => void;
}) {
  const active = sortKey === k;
  const arrow = active ? (sortDir === 'asc' ? '▲' : '▼') : '';
  return (
    <th
      onClick={() => toggleSort(k)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
    >
      {label}
      {active && (
        <span style={{ marginLeft: 4, fontSize: 9, color: '#888' }}>
          {arrow}
        </span>
      )}
    </th>
  );
}


function ResponseRow({
  r, sessionKeys,
}: {
  r: FormResponse;
  sessionKeys: string[];
}) {
  // The "forwarded" annotation surfaces when the user typed an email
  // that doesn't match the recipient's enrolled email -- usually
  // means they forwarded the invitation to someone else who then
  // responded on the original recipient's behalf.
  const forwarded =
    r.submitted_email &&
    r.submitted_email.toLowerCase() !== r.recipient_email.toLowerCase();

  return (
    <tr>
      <td className={styles.code} style={{ whiteSpace: 'nowrap' }}>
        {formatEt(r.submitted_at)}
      </td>
      <td>{r.recipient_name || '—'}</td>
      <td className={styles.code}>
        {r.recipient_email}
        {forwarded && (
          <div
            style={{ fontSize: 10, color: '#7B5400', marginTop: 2 }}
            title={`Form submitted with ${r.submitted_email}, but the enrolled recipient's email is ${r.recipient_email}.`}
          >
            forwarded · typed {r.submitted_email}
          </div>
        )}
      </td>
      <td>{r.recipient_company || '—'}</td>
      <td><AttendingBadge attending={r.attending} responseType={r.response_type} /></td>
      {sessionKeys.map((k) => (
        <td key={k}><SessionChip answer={r.sessions?.[k] ?? null} /></td>
      ))}
      <td>{r.dietary || <span style={{ color: '#999' }}>—</span>}</td>
      <td style={{ maxWidth: 320 }}>
        {r.comments ? (
          <span title={r.comments}>{r.comments}</span>
        ) : (
          <span style={{ color: '#999' }}>—</span>
        )}
      </td>
      <td>{r.campaign_name}</td>
    </tr>
  );
}


/**
 * One session's answer. A row submitted to a DIFFERENT form (or before the
 * session existed) simply has no answer for this key — a dash, not a No.
 */
function SessionChip({ answer }: { answer: 'yes' | 'no' | null }) {
  if (answer !== 'yes' && answer !== 'no') {
    return <span className={styles.sessionChipNone}>—</span>;
  }
  return (
    <span
      className={`${styles.sessionChip} ${
        answer === 'yes' ? styles.sessionChipYes : styles.sessionChipNo
      }`}
    >
      {answer === 'yes' ? 'YES' : 'NO'}
    </span>
  );
}


function AttendingBadge({
  attending,
  responseType,
}: {
  attending: 'yes' | 'no' | null;
  responseType: string;
}) {
  // attending = null only when response_type isn't an attending pair
  // (rsvp_virtual / custom). Surface the raw type so admins still see
  // what was submitted instead of a blank cell.
  if (attending === null) {
    return (
      <span
        style={{
          display: 'inline-block', padding: '2px 8px', borderRadius: 10,
          background: '#F0F0EC', color: '#666', fontSize: 11,
          fontFamily: "'DM Mono', monospace",
          textTransform: 'uppercase', letterSpacing: '0.04em',
        }}
        title="Non-attending response type — see raw value"
      >
        {responseType.replace(/^rsvp_/, '').replace(/_/g, ' ')}
      </span>
    );
  }
  const isYes = attending === 'yes';
  return (
    <span
      style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 10,
        background: isYes ? '#D5EFD3' : '#F4DEDA',
        color: isYes ? '#1F5E1F' : '#8A2A1F',
        fontSize: 11,
        fontFamily: "'DM Mono', monospace",
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }}
    >
      {isYes ? 'YES' : 'NO'}
    </span>
  );
}
